/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect } from 'react';
import { FretPosition } from '../Fretboard';
import FretboardQuiz from './FretboardQuiz';
import { QuizBannerState } from './NoteRecognitionQuiz';

interface FretboardPracticeProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
  tuning: string[];
  // Fretboard control props
  fretboardInteractive: boolean;
  setFretboardInteractive: (active: boolean) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  correctUserFretboardAnswers: FretPosition[];
  setCorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  incorrectUserFretboardAnswers: FretPosition[];
  setIncorrectUserFretboardAnswers: (positions: FretPosition[]) => void;
  fretRange?: { start: number; end: number };
  setFretRange?: (range: { start: number; end: number }) => void;
  isStandardTuning: boolean;
  // Functions to clear the fretboard
  onClearFretboard?: () => void;
  /** When true, shows compact overlay banner instead of full Card UI */
  isMobile?: boolean;
  /** Callback to expose quiz state to parent for mobile banner rendering */
  onQuizStateChange?: (state: QuizBannerState) => void;
}

const FretboardPractice: React.FC<FretboardPracticeProps> = ({
  currentKey,
  currentScale,
  noteCollection,
  tuning,
  fretboardInteractive,
  setFretboardInteractive,
  setRootNotePosition,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  correctUserFretboardAnswers,
  setCorrectUserFretboardAnswers,
  incorrectUserFretboardAnswers,
  setIncorrectUserFretboardAnswers,
  fretRange = { start: 0, end: 12 },
  setFretRange,
  isStandardTuning,
  onClearFretboard,
  isMobile = false,
  onQuizStateChange
}) => {

  // Create a clean noteCollection with just open strings
  const cleanNoteCollection = {
    notes: [],
    intervals: [],
    rootNote: '',
    keySignature: 'C',
    mode: 'clean'
  };

  // Clear the fretboard state when this component mounts (but don't disable interactive mode)
  useEffect(() => {
    // Note: Don't call onClearFretboard() here as it disables interactive mode,
    // and FretboardQuiz will enable it. Just clear the visual state.
    setUserFretboardAnswers([]);
    setRootNotePosition(null);
    setHighlightedFretPositions([]);
  }, []); // Empty dependency array - only run on mount

  return (
    <FretboardQuiz
      currentKey={currentKey}
      currentScale={currentScale}
      noteCollection={cleanNoteCollection}
      tuning={tuning}
      fretboardInteractive={fretboardInteractive}
      setFretboardInteractive={setFretboardInteractive}
      setRootNotePosition={setRootNotePosition}
      userFretboardAnswers={userFretboardAnswers}
      setUserFretboardAnswers={setUserFretboardAnswers}
      setHighlightedFretPositions={setHighlightedFretPositions}
      correctUserFretboardAnswers={correctUserFretboardAnswers}
      setCorrectUserFretboardAnswers={setCorrectUserFretboardAnswers}
      incorrectUserFretboardAnswers={incorrectUserFretboardAnswers}
      setIncorrectUserFretboardAnswers={setIncorrectUserFretboardAnswers}
      fretRange={fretRange}
      setFretRange={setFretRange}
      isStandardTuning={isStandardTuning}
      isMobile={isMobile}
      onQuizStateChange={onQuizStateChange}
    />
  );
};

export default FretboardPractice;